import { motion } from "motion/react"
import { Check } from "lucide-react"
import { Link } from "react-router-dom"

export function AuthSuccess({
  email,
  purpose,
  onResend,
}: {
  email: string
  purpose: "signup" | "login"
  onResend?: () => void
}) {
  return (
    <div className="space-y-7 text-center">
      <HexCheck />

      <div className="space-y-1.5">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Check your inbox
        </h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {purpose === "signup"
            ? "We just sent a sign-in link to"
            : "We just sent a sign-in link to"}
        </p>
        <p className="break-all pt-0.5 font-mono text-sm text-foreground">
          {email}
        </p>
      </div>

      <div className="space-y-3 border-t border-border/40 pt-5">
        <p className="text-2xs uppercase tracking-[0.18em] text-muted-foreground/60">
          What to do next
        </p>
        <ul className="space-y-2 text-left text-sm text-muted-foreground">
          <li className="flex gap-2.5">
            <DotMark />
            <span>Open the email and click the sign-in link.</span>
          </li>
          <li className="flex gap-2.5">
            <DotMark />
            <span>The link works for 15 minutes and is single-use.</span>
          </li>
          <li className="flex gap-2.5">
            <DotMark />
            <span>No email? Check spam, or request another link below.</span>
          </li>
        </ul>
      </div>

      <div className="space-y-3 pt-1">
        {onResend && (
          <button
            type="button"
            onClick={onResend}
            className="text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            Send another link
          </button>
        )}
        <div className="text-2xs uppercase tracking-[0.18em] text-muted-foreground/50">
          <Link
            to={purpose === "signup" ? "/login" : "/signup"}
            className="transition-colors hover:text-foreground/80"
          >
            {purpose === "signup" ? "Sign in instead" : "Create an account"}
          </Link>
        </div>
      </div>
    </div>
  )
}

function HexCheck() {
  return (
    <motion.div
      initial={{ scale: 0.85, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="relative mx-auto h-16 w-16"
    >
      <div className="absolute inset-0 rounded-full bg-rose-500/20 blur-xl" />

      <svg
        viewBox="0 0 100 100"
        className="relative h-full w-full"
        aria-hidden
      >
        <defs>
          <linearGradient id="hexFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgb(244 63 94 / 0.18)" />
            <stop offset="100%" stopColor="rgb(244 63 94 / 0.06)" />
          </linearGradient>
        </defs>
        <path
          d="M50 6 L91 28 L91 72 L50 94 L9 72 L9 28 Z"
          fill="url(#hexFill)"
          stroke="rgb(244 63 94 / 0.6)"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>

      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.18, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0 flex items-center justify-center text-rose-400"
      >
        <Check className="h-7 w-7" strokeWidth={2.5} />
      </motion.div>
    </motion.div>
  )
}

function DotMark() {
  return (
    <span
      aria-hidden
      className="mt-1.5 inline-block h-1 w-1 shrink-0 rounded-full bg-rose-400/70"
    />
  )
}
